/**
 * InvestorCommunication — Phase K
 * Adhoc emails to filtered Investor Accounts or Investor Contacts using
 * approved general / investor_account templates.
 *
 * Filter UX mirrors the Custom Report Builder: a small list of pre-curated
 * fields (returned by /api/investor-communication/filterable-fields), each
 * with a free-text "contains" input. The recipient list updates live.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Mail, Send, AlertCircle, CheckCircle, Loader2, X, StopCircle,
  Eye, RefreshCw, Users, UserCheck, FileText, Filter, Trash2, Search
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import EmailPreviewModal from '../components/email/EmailPreviewModal';
import TeamMemberPicker from '../components/email/TeamMemberPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:    { cls: 'bg-gray-100 text-gray-700',     label: 'Pending' },
  running:    { cls: 'bg-blue-100 text-blue-700',     label: 'Sending...' },
  completed:  { cls: 'bg-green-100 text-green-800',   label: 'Completed' },
  aborted:    { cls: 'bg-yellow-100 text-yellow-800', label: 'Aborted' },
  failed:     { cls: 'bg-red-100 text-red-800',       label: 'Failed' },
};
const REC_STATUS_META = {
  pending: { cls: 'bg-gray-100 text-gray-600',     label: 'Pending' },
  sent:    { cls: 'bg-green-100 text-green-700',   label: 'Sent' },
  failed:  { cls: 'bg-red-100 text-red-700',       label: 'Failed' },
  skipped: { cls: 'bg-yellow-100 text-yellow-700', label: 'Skipped' },
  aborted: { cls: 'bg-yellow-100 text-yellow-700', label: 'Aborted' },
};

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
};

// One filter row: pick a field from the catalog + a "contains" value.
const FilterRow = ({ idx, fields, filter, onChange, onRemove }) => {
  return (
    <div className="flex items-center gap-2 mb-2" data-testid={`comm-filter-row-${idx}`}>
      <select
        value={filter.field}
        onChange={e => onChange({ ...filter, field: e.target.value })}
        className="px-2 py-1.5 border border-gray-300 rounded text-sm flex-1 max-w-[230px]"
        data-testid={`comm-filter-field-${idx}`}
      >
        <option value="">— pick a field —</option>
        {fields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
      </select>
      <span className="text-xs text-gray-400">contains</span>
      <input
        type="text"
        value={filter.value}
        onChange={e => onChange({ ...filter, value: e.target.value })}
        placeholder="Type to filter…"
        className="px-2 py-1.5 border border-gray-300 rounded text-sm flex-1"
        data-testid={`comm-filter-value-${idx}`}
        disabled={!filter.field}
      />
      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 text-gray-400 hover:text-red-600"
        title="Remove filter"
        data-testid={`comm-filter-remove-${idx}`}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
};

const InvestorCommunication = () => {
  const { user: currentUser } = useAuth();
  const [mode, setMode] = useState('accounts'); // 'accounts' | 'contacts'
  const [funds, setFunds] = useState([]);
  const [fundId, setFundId] = useState('');

  const [filterableFields, setFilterableFields] = useState({ accounts: [], contacts: [] });
  const [filterRows, setFilterRows] = useState([]); // [{ field, value }]

  const [recipients, setRecipients] = useState([]);
  const [recipientStats, setRecipientStats] = useState({ total: 0, deliverable: 0, skipped_no_email: 0 });
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [bccSelf, setBccSelf] = useState(false);
  const [dateFormat, setDateFormat] = useState('dd mmmm yyyy');
  const [numberFormat, setNumberFormat] = useState('western');
  const [selectedIds, setSelectedIds] = useState([]);
  const [ccUserIds, setCcUserIds] = useState([]);
  const [bccUserIds, setBccUserIds] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeJob, setActiveJob] = useState(null);
  const [history, setHistory] = useState([]);

  const senderEnabled = !!currentUser?.email_sending_enabled;
  const sendingActive = activeJob && (activeJob.status === 'pending' || activeJob.status === 'running');
  const progressPct = activeJob && activeJob.total > 0
    ? Math.round(((activeJob.sent || 0) + (activeJob.failed || 0) + (activeJob.skipped || 0)) / activeJob.total * 100)
    : 0;
  const allSelectedOnPage = recipients.length > 0 && recipients.every(r => selectedIds.includes(r.id));

  // Phase L3 — client-side search overlay on the recipient list. Lets the
  // user narrow a long account list by typing without re-running the server
  // filter (which is cheaper for a 200-row list than a network round-trip).
  const visibleRecipients = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter(r => {
      const blob = [
        r.label, r.email, r.pan,
        ...(r.cc_emails || []),
        ...(r.fund_names || []),
      ].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [recipients, searchQuery]);
  const allVisibleSelected = visibleRecipients.length > 0
    && visibleRecipients.every(r => selectedIds.includes(r.id));

  // ---------- bootstrap ----------
  useEffect(() => {
    (async () => {
      try {
        const [fundsRes, fieldsRes, tplRes] = await Promise.all([
          axios.get(`${API}/funds`),
          axios.get(`${API}/investor-communication/filterable-fields`),
          axios.get(`${API}/email-templates?scope=approved&section=investor_communication`),
        ]);
        setFunds(fundsRes.data || []);
        setFilterableFields(fieldsRes.data || { accounts: [], contacts: [] });
        setTemplates((tplRes.data || []).filter(t => ['general', 'investor_account'].includes(t.context_type)));
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to bootstrap');
      }
    })();
    loadHistory();
  }, []);

  useEffect(() => { loadRecipients(); /* eslint-disable-next-line */ }, [mode, fundId, JSON.stringify(filterRows)]);

  // poll active job
  useEffect(() => {
    if (!activeJob) return;
    if (['completed', 'aborted', 'failed'].includes(activeJob.status)) return;
    const tick = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/investor-communication/jobs/${activeJob.id}`);
        setActiveJob(data);
        if (['completed', 'aborted', 'failed'].includes(data.status)) loadHistory();
      } catch { /* ignore */ }
    }, 1500);
    return () => clearInterval(tick);
    // eslint-disable-next-line
  }, [activeJob?.id, activeJob?.status]);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/investor-communication/history`);
      setHistory(data || []);
    } catch { /* ignore */ }
  };

  const filterPayload = useMemo(() => {
    const out = {};
    filterRows.forEach(r => {
      if (r.field && (r.value || '').trim()) out[r.field] = r.value.trim();
    });
    return out;
  }, [filterRows]);

  const loadRecipients = async () => {
    setLoadingRecipients(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/investor-communication/recipients`, {
        mode,
        fund_id: fundId || null,
        columns: [],
        filters: filterPayload,
      });
      setRecipients(data.rows || []);
      setRecipientStats({
        total: data.total || 0,
        deliverable: data.deliverable || 0,
        skipped_no_email: data.skipped_no_email || 0,
      });
      // Drop selections that no longer match the current filter
      setSelectedIds(prev => prev.filter(id => (data.rows || []).some(r => r.id === id)));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  const fields = mode === 'accounts' ? filterableFields.accounts : filterableFields.contacts;

  const addFilter = () => setFilterRows(rows => [...rows, { field: '', value: '' }]);
  const updateFilter = (i, val) => setFilterRows(rows => rows.map((r, idx) => idx === i ? val : r));
  const removeFilter = (i) => setFilterRows(rows => rows.filter((_, idx) => idx !== i));
  const clearFilters = () => setFilterRows([]);

  const switchMode = (m) => {
    setMode(m);
    setFilterRows([]);     // filter fields differ between modes — start clean
    setSelectedIds([]);
  };

  const toggleRow = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => {
    // Only toggles rows that match the current search filter — clicking
    // "Select all" inside a search shouldn't sweep in hidden rows.
    const ids = visibleRecipients.filter(r => r.email).map(r => r.id);
    if (ids.length > 0 && ids.every(id => selectedIds.includes(id))) {
      setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...ids])));
    }
  };

  const handleSend = async () => {
    setError(''); setSuccess('');
    if (!selectedTemplate) { setError('Pick an approved template first.'); return; }
    if (selectedIds.length === 0) { setError('Select at least one recipient.'); return; }
    try {
      const { data } = await axios.post(`${API}/investor-communication/send`, {
        template_id: selectedTemplate,
        mode,
        recipient_ids: selectedIds,
        fund_id: fundId || null,
        bcc_self: bccSelf,
        date_format: dateFormat,
        number_format: numberFormat,
        cc_user_ids: ccUserIds,
        bcc_user_ids: bccUserIds,
      });
      const { data: job } = await axios.get(`${API}/investor-communication/jobs/${data.job_id}`);
      setActiveJob(job);
      setSelectedIds([]);
      const skipped = data.skipped_no_email || [];
      setSuccess(`Queued ${data.total} email(s).` + (skipped.length ? ` Skipped ${skipped.length} recipient(s) without an email.` : ''));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start send');
    }
  };

  const handleAbort = async () => {
    if (!activeJob) return;
    if (!window.confirm('Abort sending? Already-sent emails cannot be unsent.')) return;
    try {
      await axios.post(`${API}/investor-communication/jobs/${activeJob.id}/abort`);
      setSuccess('Abort requested. Stopping after the current recipient...');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to abort'); }
  };

  const previewRecipientsList = useMemo(() =>
    recipients
      .filter(r => selectedIds.includes(r.id))
      .map(r => ({ id: r.id, account_name: r.label })),
    [recipients, selectedIds]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="investor-communication-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center"><Mail className="mr-2 text-blue-600" size={22} /> Investor Communication</h1>
        <p className="text-sm text-gray-600 mt-1">Send adhoc emails to investor accounts or individual contacts using your approved <strong>General</strong> or <strong>Investor Account</strong> templates.</p>
      </div>

      {!senderEnabled && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start" data-testid="comm-disabled-notice">
          <AlertCircle className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-yellow-900">
            <p className="font-semibold">You can't send emails from your account yet.</p>
            <p className="mt-1">Ask an Admin to enable "Allow this user to send emails" for you in <strong>User Management</strong>, then save your SMTP credentials in <Link className="underline text-blue-700" to="/email-config">Email Configuration</Link>.</p>
          </div>
        </div>
      )}

      {/* Mode toggle */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Recipient mode</h3>
        <div className="flex items-center gap-2" role="tablist">
          <button
            type="button"
            onClick={() => switchMode('accounts')}
            className={`px-4 py-2 rounded-lg border-2 text-sm flex items-center transition-colors ${
              mode === 'accounts' ? 'border-blue-600 bg-blue-50 text-blue-700 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
            data-testid="comm-mode-accounts"
          >
            <Users size={15} className="mr-2" /> Investor Accounts
            <span className="ml-2 text-xs text-gray-400">(one email per account, To = primary contacts)</span>
          </button>
          <button
            type="button"
            onClick={() => switchMode('contacts')}
            className={`px-4 py-2 rounded-lg border-2 text-sm flex items-center transition-colors ${
              mode === 'contacts' ? 'border-blue-600 bg-blue-50 text-blue-700 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
            data-testid="comm-mode-contacts"
          >
            <UserCheck size={15} className="mr-2" /> Investor Contacts
            <span className="ml-2 text-xs text-gray-400">(one email per individual)</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900 flex items-center"><Filter size={14} className="mr-2 text-gray-500" /> Filters</h3>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">{recipientStats.total} match · {recipientStats.deliverable} have email</span>
            <button onClick={loadRecipients} className="text-gray-500 hover:text-gray-800 flex items-center" data-testid="comm-recipients-refresh"><RefreshCw size={12} className="mr-1" /> Refresh</button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fund <span className="text-gray-400 ml-1">(optional)</span></label>
            <select value={fundId} onChange={e => setFundId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="comm-fund-select">
              <option value="">All funds</option>
              {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div className="text-xs text-gray-500 self-end">
            {mode === 'contacts'
              ? 'When a Fund is picked, only contacts attached to accounts on that fund are listed.'
              : 'When a Fund is picked, only accounts on that fund are listed.'}
          </div>
        </div>
        {filterRows.length === 0 && (
          <p className="text-xs text-gray-400 italic mb-2">No filters yet — all matching {mode} are listed below.</p>
        )}
        {filterRows.map((f, i) => (
          <FilterRow key={i} idx={i} fields={fields} filter={f} onChange={(v) => updateFilter(i, v)} onRemove={() => removeFilter(i)} />
        ))}
        <div className="flex items-center gap-2 mt-1">
          <button type="button" onClick={addFilter} className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200" data-testid="comm-add-filter">+ Add filter</button>
          {filterRows.length > 0 && (
            <button type="button" onClick={clearFilters} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-800" data-testid="comm-clear-filters">Clear all</button>
          )}
        </div>
      </div>

      {/* Composer + recipient list */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-bold text-gray-900 flex items-center mb-4">
          <Mail size={16} className="mr-2 text-blue-600" /> Compose
        </h3>

        {error && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800 flex items-start" data-testid="comm-error">
            <AlertCircle size={14} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" /> {error}
          </div>
        )}
        {success && (
          <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800 flex items-center" data-testid="comm-success">
            <CheckCircle size={14} className="text-green-600 mr-2" /> {success}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Approved Template *
              <span className="text-gray-400 ml-1">(General or Investor-Account context only)</span>
            </label>
            <select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="comm-template-select">
              <option value="">— Pick a template —</option>
              {templates.length === 0 && <option disabled>(no approved General/Investor-Account templates yet)</option>}
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name} · {t.context_type === 'general' ? 'General' : 'Investor Account'}</option>
              ))}
            </select>
            {templates.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Need to <Link className="text-blue-600 underline" to="/email-templates/new">create one</Link> first and have an Admin approve it.
              </p>
            )}
          </div>
          <div className="space-y-2 pt-1">
            <label className="flex items-center cursor-pointer">
              <input type="checkbox" checked={bccSelf} onChange={e => setBccSelf(e.target.checked)} className="h-4 w-4 text-blue-600 rounded" data-testid="comm-bcc-self" />
              <span className="ml-2 text-sm text-gray-700">BCC me on every email</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date Format</label>
            <select value={dateFormat} onChange={e => setDateFormat(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="comm-date-format">
              <option value="dd mmmm yyyy">25 January 2026</option>
              <option value="dd-mm-yyyy">25-01-2026</option>
              <option value="mm-dd-yyyy">01-25-2026</option>
              <option value="dd/mm/yyyy">25/01/2026</option>
              <option value="mm/dd/yyyy">01/25/2026</option>
              <option value="yyyy-mm-dd">2026-01-25</option>
              <option value="mmmm dd, yyyy">January 25, 2026</option>
              <option value="dd-mmmm-yyyy">25-January-2026</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Number Format</label>
            <select value={numberFormat} onChange={e => setNumberFormat(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="comm-number-format">
              <option value="western">Western (1,000,000)</option>
              <option value="indian">Indian (10,00,000)</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <TeamMemberPicker label="CC team members" value={ccUserIds} onChange={setCcUserIds} placeholder="Add teammates to Cc on every email…" testid="comm-cc-team" />
          <TeamMemberPicker label="BCC team members" value={bccUserIds} onChange={setBccUserIds} placeholder="Add teammates to Bcc on every email…" testid="comm-bcc-team" />
        </div>

        {/* Recipient table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs font-semibold text-gray-700">Recipients ({selectedIds.length} of {recipients.length} selected · {recipientStats.skipped_no_email} have no email and are skipped)</span>
            <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-[420px]">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder={`Search ${mode === 'accounts' ? 'accounts' : 'contacts'} by name, email, PAN, fund…`}
                  className="w-full pl-7 pr-7 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                  data-testid="comm-recipient-search"
                />
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-1"
                    data-testid="comm-recipient-search-clear"
                    aria-label="Clear search"
                  ><X size={11} /></button>
                )}
              </div>
              <button onClick={toggleAll} className="text-xs text-blue-600 hover:underline disabled:opacity-50 whitespace-nowrap" data-testid="comm-select-all" disabled={visibleRecipients.length === 0}>
                {allVisibleSelected ? 'Clear visible' : 'Select all visible'}
              </button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loadingRecipients ? (
              <p className="p-4 text-sm text-gray-500 italic text-center"><Loader2 size={14} className="inline animate-spin mr-1" />Loading...</p>
            ) : visibleRecipients.length === 0 ? (
              <p className="p-6 text-sm text-gray-500 italic text-center" data-testid="comm-recipients-empty">
                {recipients.length === 0
                  ? `No ${mode} match the current filters.`
                  : `No ${mode} match "${searchQuery}".`}
              </p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {visibleRecipients.map(r => {
                    const noEmail = !r.email;
                    return (
                    <tr key={r.id} className={`border-b border-gray-100 last:border-0 hover:bg-gray-50 ${noEmail ? 'opacity-50' : ''}`}>
                      <td className="px-3 py-2 w-8">
                        <input type="checkbox"
                          checked={selectedIds.includes(r.id)}
                          onChange={() => toggleRow(r.id)}
                          disabled={noEmail}
                          data-testid={`comm-pick-${r.id}`}
                          className="h-4 w-4 text-blue-600 rounded disabled:opacity-30" />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{r.label}</div>
                        <div className="text-xs text-gray-500 truncate max-w-md">
                          {noEmail ? <em className="text-red-600">no email</em> : <><span className="font-semibold">To:</span> {r.email}</>}
                          {(r.cc_emails || []).length > 0 && <span className="ml-2"><span className="font-semibold">Cc:</span> {(r.cc_emails || []).join(', ')}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                        {(r.fund_names || []).map(f => <span key={f} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded mr-1">{f}</span>)}
                        {r.pan && <span className="text-gray-400">PAN: {r.pan}</span>}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={() => setPreviewOpen(true)} disabled={!selectedTemplate || selectedIds.length === 0} className="px-4 py-2 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 text-sm font-semibold flex items-center disabled:opacity-50 disabled:cursor-not-allowed" data-testid="comm-preview-btn">
            <Eye size={14} className="mr-2" /> Preview
          </button>
          <button onClick={handleSend} disabled={!senderEnabled || sendingActive || templates.length === 0 || selectedIds.length === 0 || !selectedTemplate} className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold flex items-center disabled:opacity-50 disabled:cursor-not-allowed" data-testid="comm-send-btn">
            <Send size={14} className="mr-2" /> Send to {selectedIds.length || '...'} {mode === 'accounts' ? 'Account' : 'Contact'}{selectedIds.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>

      <EmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        drawdownId="adhoc"
        previewPath="investor-communication/preview"
        testSendPath="investor-communication/test-send"
        templateId={selectedTemplate}
        kind="adhoc_communication"
        recipientFieldName="recipient_id"
        recipients={previewRecipientsList}
        bccSelf={bccSelf}
        dateFormat={dateFormat}
        numberFormat={numberFormat}
        ccUserIds={ccUserIds}
        bccUserIds={bccUserIds}
        extraPayload={{ mode, fund_id: fundId || null }}
      />

      {activeJob && (
        <div className="bg-white border border-blue-300 rounded-lg p-5" data-testid="comm-active-job">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <Loader2 size={14} className={`mr-2 ${sendingActive ? 'animate-spin text-blue-600' : 'text-gray-400'}`} />
              {sendingActive ? 'Sending emails...' : 'Last send result'}
            </h3>
            <div className="flex items-center gap-2 text-xs">
              <span className={`px-2 py-0.5 rounded-full font-medium ${(STATUS_META[activeJob.status] || STATUS_META.pending).cls}`} data-testid="comm-job-status">
                {(STATUS_META[activeJob.status] || STATUS_META.pending).label}
              </span>
              {sendingActive && (
                <button onClick={handleAbort} className="flex items-center bg-red-50 text-red-700 px-2 py-1 rounded hover:bg-red-100" data-testid="comm-abort-btn">
                  <StopCircle size={12} className="mr-1" /> Abort
                </button>
              )}
              <button onClick={() => setActiveJob(null)} className="text-gray-400 hover:text-gray-700"><X size={12} /></button>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2"><div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${progressPct}%` }} /></div>
          <p className="text-xs text-gray-600 text-center">{activeJob.sent || 0} sent · {activeJob.failed || 0} failed · {activeJob.skipped || 0} skipped · of {activeJob.total} total</p>
          {!sendingActive && (activeJob.recipients || []).length > 0 && (
            <div className="mt-3 max-h-48 overflow-y-auto border border-gray-100 rounded">
              <table className="w-full text-xs">
                <tbody>
                  {(activeJob.recipients || []).map((r, i) => (
                    <tr key={i} className="border-b border-gray-50 last:border-0">
                      <td className="px-2 py-1 text-gray-700">{r.account_name}</td>
                      <td className="px-2 py-1">
                        <span className={`px-1.5 py-0.5 rounded ${(REC_STATUS_META[r.status] || REC_STATUS_META.pending).cls}`}>
                          {(REC_STATUS_META[r.status] || REC_STATUS_META.pending).label}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-gray-500">{r.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="comm-history">
          <h3 className="text-sm font-bold text-gray-900 flex items-center mb-3">
            <FileText size={14} className="mr-2 text-gray-500" /> Send History
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Mode</th>
                  <th className="px-3 py-2 text-left">Template</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Sent</th>
                  <th className="px-3 py-2 text-right">Failed</th>
                  <th className="px-3 py-2 text-right">Skipped</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map(j => (
                  <tr key={j.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-700">{fmtDate(j.created_at)}</td>
                    <td className="px-3 py-2 text-gray-700">{j.communication_mode || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{j.template_name || '—'}</td>
                    <td className="px-3 py-2 text-right">{j.total}</td>
                    <td className="px-3 py-2 text-right text-green-700 font-medium">{j.sent}</td>
                    <td className="px-3 py-2 text-right text-red-700 font-medium">{j.failed}</td>
                    <td className="px-3 py-2 text-right text-yellow-700 font-medium">{j.skipped}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${(STATUS_META[j.status] || STATUS_META.pending).cls}`}>{(STATUS_META[j.status] || STATUS_META.pending).label}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorCommunication;
